"use client";

import { Minus, Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function WhichMetalToUse() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: panelsRef,
    isVisible: panelsVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const tiers = [{
    number: "01",
    name: "STANDARD MARKING",
    descriptor: "Essential identification solutions for industrial compliance and basic asset tracking.",
    featured: false,
    price: "From $150",
    features: [{
      included: true,
      text: "Standard phenolic engraving"
    }, {
      included: true,
      text: "Basic aluminum tags"
    }, {
      included: true,
      text: "Standard font selection"
    }, {
      included: true,
      text: "Single-color infill"
    }, {
      included: false,
      text: "Custom logo integration"
    }, {
      included: false,
      text: "Advanced DPM scanning"
    }]
  }, {
    number: "02",
    name: "PRECISION ENGRAVING",
    descriptor: "High-durability marking for demanding environments and complex technical specifications.",
    featured: true,
    price: "From $450",
    features: [{
      included: true,
      text: "Deep-laser metal engraving"
    }, {
      included: true,
      text: "Custom stainless steel plates"
    }, {
      included: true,
      text: "Precision vector graphics"
    }, {
      included: true,
      text: "Multi-stage chemical etching"
    }, {
      included: true,
      text: "Custom logo & branding"
    }, {
      included: true,
      text: "DPM barcode verification"
    }, {
      included: false,
      text: "Full facility audit support"
    }]
  }, {
    number: "03",
    name: "ENTERPRISE SOLUTIONS",
    descriptor: "Comprehensive identification systems engineered for large-scale infrastructure projects.",
    featured: false,
    price: "Custom Quote",
    features: [{
      included: true,
      text: "Full-scale facility labeling"
    }, {
      included: true,
      text: "Automated submittal packages"
    }, {
      included: true,
      text: "Bespoke material sourcing"
    }, {
      included: true,
      text: "Estate-wide asset management"
    }, {
      included: true,
      text: "Advanced DPM & QR integration"
    }, {
      included: true,
      text: "On-site installation support"
    }, {
      included: true,
      text: "Compliance documentation"
    }, {
      included: true,
      text: "Dedicated account engineering"
    }]
  }];
  return <section data-section-id="3547" id="which-metal-to-use" className="relative bg-background text-foreground overflow-hidden py-24 md:py-32 lg:py-40">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute -top-40 -left-40 h-[600px] w-[600px] rounded-lg" style={{
      background: "radial-gradient(circle, hsla(45, 68%, 47%, 0.08) 0%, transparent 70%)"
    }} />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-8">
            <p className="font-mono text-xs tracking-widest text-muted-foreground mb-6 md:mb-8">
              [ 02 / SOLUTIONS ]
            </p>
            <h2 className="font-default font-bold text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight uppercase">
              Select Your
              <br />
              <span className="text-primary">Precision.</span>
            </h2>
          </div>
          <div className="lg:col-span-4 flex items-end">
            <p className="font-serif text-lg md:text-xl leading-relaxed text-muted-foreground">
              Professional marking solutions tailored to your technical requirements, from standard tags to enterprise-grade identification.
            </p>
          </div>
        </div>

        {}
        <div ref={panelsRef} className={`mt-24 md:mt-32 grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-6 items-stretch transition-all duration-700 ease-out delay-150 ${panelsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {tiers.map((tier, i) => <div data-index={i} key={tier.number} className={`group relative flex flex-col backdrop-blur-xl bg-card shadow-3xl transition-all duration-500 ease-out hover:-translate-y-2 hover:shadow-4xl ${tier.featured ? "border border-primary lg:-mt-16 lg:mb-0" : "border border-border"}`} style={{
          background: "linear-gradient(180deg, hsla(0, 0%, 100%, 0.03) 0%, hsla(0, 0%, 100%, 0.01) 100%)"
        }}>
              {tier.featured && <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <div className="bg-primary px-4 py-1.5">
                    <span className="font-mono text-[10px] tracking-widest text-primary-foreground uppercase whitespace-nowrap">
                      [ Recommended ]
                    </span>
                  </div>
                </div>}

              <div className="flex flex-col flex-1 p-8 md:p-10">
                {}
                <div className={`font-default font-bold text-7xl md:text-8xl leading-none tracking-tight ${tier.featured ? "text-primary" : "text-foreground"}`}>
                  {tier.number}
                </div>

                {}
                <h3 className="font-default font-bold text-2xl md:text-3xl tracking-tight uppercase mt-8">
                  {tier.name}
                </h3>

                {}
                <p className="font-serif text-base md:text-lg leading-relaxed text-muted-foreground mt-3">
                  {tier.descriptor}
                </p>

                {}
                <ul className="mt-10 space-y-4 flex-1">
                  {tier.features.map((feature, j) => <li data-index={j} key={j} className="flex items-start gap-3">
                      {feature.included ? <Plus className="size-4 mt-1 shrink-0 text-primary" strokeWidth={1.5} /> : <Minus className="size-4 mt-1 shrink-0 text-muted-foreground/50" strokeWidth={1.5} />}
                      <span className={`font-serif text-base leading-snug ${feature.included ? "text-foreground" : "text-muted-foreground/60 line-through decoration-muted-foreground/30"}`}>
                        {feature.text}
                      </span>
                    </li>)}
                </ul>

                {}
                <div className="mt-10 h-px w-full bg-border" />

                {}

                {}
                <div className="mt-8">
                  {tier.featured ? <Button asChild className="w-full h-12 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 font-mono text-xs tracking-widest uppercase shadow-lg">
                      <Link to="/ContactPage">Get Started</Link>
                    </Button> : <Button asChild variant="outline" className="w-full h-12 rounded-lg border-border bg-transparent text-foreground hover:bg-primary hover:text-primary-foreground font-mono text-xs tracking-widest uppercase">
                      <Link to="/ContactPage">Get Started</Link>
                    </Button>}
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}